/**
 * CreateProjectModal.js
 * 
 * Modal for creating a new project from the Scoping Workspace.
 * Supports:
 * - Project name (required)
 * - Portfolio/Program selection (optional)
 * - Owner selection (optional)
 * - Hierarchy mode (Agile/Phase-based)
 * - Start/End dates (optional)
 * - Description (optional)
 */

import React, { useState, useEffect } from 'react';
import { 
  X, FolderKanban, Building2, Users, Calendar, FileText, 
  Layers, ChevronDown, Loader2, CheckCircle
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

const CreateProjectModal = ({ 
  isOpen, 
  onClose, 
  onProjectCreated,
  portfolios = [],
  programs = [],
  users = []
}) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  
  const [formData, setFormData] = useState({
    name: '',
    portfolio_id: '',
    program_id: '',
    owner_id: user?.id || '',
    hierarchy_mode: 'agile',
    start_date: '',
    end_date: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Filter programs based on selected portfolio
  const filteredPrograms = formData.portfolio_id 
    ? programs.filter(p => p.portfolio_id === formData.portfolio_id)
    : programs;
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        portfolio_id: '',
        program_id: '',
        owner_id: user?.id || '',
        hierarchy_mode: tenant?.config?.default_hierarchy_mode || 'agile',
        start_date: '',
        end_date: '',
        description: ''
      });
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, user, tenant]);
  
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Clear program if portfolio changes
      if (field === 'portfolio_id' && value !== prev.portfolio_id) {
        updated.program_id = '';
      }
      
      return updated;
    });
    setError(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const tenantId = user?.tenant?.id || user?.tenant_id || tenant?.id;
      
      const payload = {
        name: formData.name.trim(),
        tenant_id: tenantId,
        portfolio_id: formData.portfolio_id || null,
        program_id: formData.program_id || null,
        owner_id: formData.owner_id || null,
        hierarchy_mode: formData.hierarchy_mode,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        description: formData.description?.trim() || null
      };
      
      const response = await api.createProjectForScoping(payload);
      
      setSuccess(true);
      
      // Small delay to show success state
      setTimeout(() => {
        onProjectCreated?.(response.data);
        onClose();
      }, 800);
      
    } catch (err) {
      console.error('Failed to create project:', err);
      setError(err.response?.data?.detail || 'Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" data-testid="create-project-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create New Project</h2>
              <p className="text-sm text-gray-500">Start scoping a new project</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter project name"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              autoFocus
              disabled={loading || success}
              data-testid="project-name-input"
            />
          </div>
          
          {/* Portfolio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                Portfolio
              </div>
            </label>
            <div className="relative">
              <select
                value={formData.portfolio_id}
                onChange={(e) => handleChange('portfolio_id', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white"
                disabled={loading || success}
                data-testid="portfolio-select"
              >
                <option value="">Select portfolio (optional)</option>
                {portfolios.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          
          {/* Program */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-gray-400" />
                Program
              </div>
            </label>
            <div className="relative">
              <select
                value={formData.program_id}
                onChange={(e) => handleChange('program_id', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white"
                disabled={loading || success || !filteredPrograms.length}
                data-testid="program-select"
              >
                <option value="">Select program (optional)</option>
                {filteredPrograms.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {formData.portfolio_id && !filteredPrograms.length && (
              <p className="text-xs text-amber-600 mt-1">No programs in selected portfolio</p>
            )}
          </div>
          
          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                Project Owner
              </div>
            </label>
            <div className="relative">
              <select
                value={formData.owner_id}
                onChange={(e) => handleChange('owner_id', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white"
                disabled={loading || success}
                data-testid="owner-select"
              >
                <option value="">Select owner (optional)</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.display_name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          
          {/* Hierarchy Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hierarchy Mode
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleChange('hierarchy_mode', 'agile')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                  formData.hierarchy_mode === 'agile'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                disabled={loading || success}
                data-testid="hierarchy-agile-btn"
              >
                <div className="font-medium">Agile</div>
                <div className="text-xs text-gray-500 mt-1">Epics → Features → Stories</div>
              </button>
              <button
                type="button"
                onClick={() => handleChange('hierarchy_mode', 'phase')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                  formData.hierarchy_mode === 'phase'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                disabled={loading || success}
                data-testid="hierarchy-phase-btn"
              >
                <div className="font-medium">Phase-based</div>
                <div className="text-xs text-gray-500 mt-1">Phases → Tasks → Subtasks</div>
              </button>
            </div>
          </div>
          
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  Start Date
                </div>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                disabled={loading || success}
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  End Date
                </div>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                disabled={loading || success}
                data-testid="end-date-input"
              />
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                Description
              </div>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief project description (optional)"
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
              disabled={loading || success}
              data-testid="description-input"
            />
          </div>
          
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {/* Success message */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Project created successfully! Redirecting to scoping...
            </div>
          )}
        </form>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || success || !formData.name.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="create-project-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : success ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Created!
              </>
            ) : (
              'Create Project'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
